import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Shield, CheckCircle, Play, Users, Clock, Target, Award } from 'lucide-react';

const iconMap = {
  "Workers Trained": Users,
  "Course Hours": Clock,
  "Interactive Scenarios": Target,
  "Compliance Rate": Award
};

// Animated counter component
const AnimatedCounter = ({ value, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  // Parse numeric value from string like "8,500+" or "40+"
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));
  const hasPlus = value.includes('+');
  const hasComma = value.includes(',');
  const hasPercent = value.includes('%');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * numericValue);
      
      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, numericValue, duration]);

  // Format the number
  const formatNumber = (num) => {
    if (hasComma) {
      return num.toLocaleString();
    }
    return num.toString();
  };

  return (
    <span ref={ref}>
      {formatNumber(count)}{hasPlus ? '+' : ''}{hasPercent ? '%' : ''}{suffix}
    </span>
  );
};

const courseStats = [
  { value: "8,500+", label: "Workers Trained" },
  { value: "40+", label: "Course Hours" },
  { value: "15+", label: "Interactive Scenarios" },
  { value: "98%", label: "Compliance Rate" }
];

const Hero = ({ onStartCourse, onWatchPreview }) => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1659353588972-f3be41ae0834?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHx3b3JrcGxhY2UlMjBzYWZldHklMjB0cmFpbmluZ3xlbnwwfHx8fDE3Njg2OTAyNjJ8MA&ixlib=rb-4.1.0&q=85&w=1920"
          alt="Safety professionals discussing workplace safety"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70" />
      </div>
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-8 animate-fade-in">
            <Shield className="w-4 h-4" />
            <span>Free OSHA Compliance Training</span>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Master{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              Workplace Safety
            </span>
            <br />& OSHA Compliance
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Comprehensive OSHA training with interactive scenarios, quizzes, and real-world applications. 
            Protect your team and stay compliant.
          </p>

          {/* Benefits list */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {[
              'Industry-recognized content',
              'Interactive scenarios & quizzes',
              'Certificate of completion'
            ].map((benefit, index) => (
              <div key={index} className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-sm md:text-base">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button 
              onClick={onStartCourse}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-300 hover:shadow-emerald-500/40 hover:-translate-y-0.5"
            >
              Start Free Course
            </Button>
            <Button 
              variant="outline"
              onClick={onWatchPreview}
              className="border-slate-600 text-slate-300 hover:bg-slate-800 px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              <Play className="w-5 h-5 mr-2" />
              Watch Preview
            </Button>
          </div>

          {/* Stats with animated counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {courseStats.map((stat, index) => {
              const IconComponent = iconMap[stat.label] || Shield;
              return (
                <div 
                  key={index} 
                  className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 hover:border-emerald-500/30 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-center justify-center mb-2">
                    <IconComponent className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-white">
                    <AnimatedCounter value={stat.value} duration={2000 + index * 200} />
                  </div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#0f172a"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
